import {
  check,
  doublePrecision,
  foreignKey,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { food, foodMeta, foodServing } from "../food/food.sql";
import { recipe } from "../recipe/recipe.sql";
import {
  recipeVariationKinds,
  recipeVariationPatchOps,
  type RecipeVariationDetailsPatch,
  type RecipeVariationIngredientPatchData,
  type RecipeVariationStepPatchData,
} from "./types";
import { nutrient } from "../nutrient/nutrient.sql";

/**
 * Recipe-variation persistence schema.
 *
 * Invariants:
 * - Variant rows are versioned and linked to both base food and recipe versions.
 * - Patch tables enforce create/update/delete selector constraints via SQL checks.
 *
 * @see `packages/core/docs/database.md`
 */
export const recipeVariationKind = pgEnum("recipe_variation_kind", recipeVariationKinds);

export const recipeVariation = pgTable(
  "recipe_variation",
  {
    id: uuid("id").notNull(),
    version: integer("version").notNull(),
    recipeId: uuid("recipe_id").notNull(),
    recipeVersion: integer("recipe_version").notNull(),
    kind: recipeVariationKind("kind").notNull(),
    detailsPatch: jsonb("details_patch").$type<RecipeVariationDetailsPatch>(),
  },
  (t) => [
    primaryKey({ name: "recipe_variation_pk", columns: [t.id, t.version] }),
    foreignKey({
      name: "recipe_variation_food_fk",
      columns: [t.id, t.version],
      foreignColumns: [food.id, food.version],
    }),
    foreignKey({
      name: "recipe_variation_recipe_fk",
      columns: [t.recipeId, t.recipeVersion],
      foreignColumns: [recipe.id, recipe.version],
    }),
  ],
);

export const recipeVariationRelations = relations(recipeVariation, ({ one, many }) => ({
  base: one(food, {
    fields: [recipeVariation.id, recipeVariation.version],
    references: [food.id, food.version],
  }),
  meta: one(foodMeta, {
    fields: [recipeVariation.id],
    references: [foodMeta.id],
  }),
  metaIfCurrent: one(foodMeta, {
    fields: [recipeVariation.id, recipeVariation.version],
    references: [foodMeta.id, foodMeta.currentVersion],
  }),
  recipe: one(recipe, {
    fields: [recipeVariation.recipeId, recipeVariation.recipeVersion],
    references: [recipe.id, recipe.version],
  }),
  ingredientPatches: many(recipeVariationIngredientPatch),
  stepPatches: many(recipeVariationStepPatch),
}));

export const recipeVariationPatchOp = pgEnum("recipe_variation_patch_op", recipeVariationPatchOps);

export const recipeVariationIngredientPatch = pgTable(
  "recipe_variation_ingredient_patch",
  {
    id: uuid("id").notNull(),
    recipeVariationId: uuid("recipe_variation_id").notNull(),
    recipeVariationVersion: integer("recipe_variation_version").notNull(),
    op: recipeVariationPatchOp("op").notNull(),
    baseFoodServingId: uuid("base_food_serving_id").references(() => foodServing.id),
    newFoodServingId: uuid("new_food_serving_id").references(() => foodServing.id),
    data: jsonb("data").$type<RecipeVariationIngredientPatchData>(),
  },
  (t) => [
    primaryKey({ name: "recipe_variation_ingredient_patch_pk", columns: [t.id] }),
    unique("recipe_variation_ingredient_patch_base_unique").on(
      t.recipeVariationId,
      t.recipeVariationVersion,
      t.baseFoodServingId,
    ),
    unique("recipe_variation_ingredient_patch_new_unique").on(
      t.recipeVariationId,
      t.recipeVariationVersion,
      t.newFoodServingId,
    ),
    check(
      "recipe_variation_ingredient_patch_base_required",
      sql`(
        (${t.op} = 'create' AND ${t.baseFoodServingId} IS NULL)
        OR
        (${t.op} IN ('update', 'delete') AND ${t.baseFoodServingId} IS NOT NULL)
      )`,
    ),
    check(
      "recipe_variation_ingredient_patch_new_required",
      sql`(
        (${t.op} = 'create' AND ${t.newFoodServingId} IS NOT NULL)
        OR
        (${t.op} = 'update')
        OR
        (${t.op} = 'delete' AND ${t.newFoodServingId} IS NULL)
      )`,
    ),
    foreignKey({
      name: "recipe_variation_ingredient_patch_variant_fk",
      columns: [t.recipeVariationId, t.recipeVariationVersion],
      foreignColumns: [recipeVariation.id, recipeVariation.version],
    }),
  ],
);

export const recipeVariationIngredientPatchRelations = relations(
  recipeVariationIngredientPatch,
  ({ one }) => ({
    variant: one(recipeVariation, {
      fields: [
        recipeVariationIngredientPatch.recipeVariationId,
        recipeVariationIngredientPatch.recipeVariationVersion,
      ],
      references: [recipeVariation.id, recipeVariation.version],
    }),
    baseServing: one(foodServing, {
      fields: [recipeVariationIngredientPatch.baseFoodServingId],
      references: [foodServing.id],
    }),
    newServing: one(foodServing, {
      fields: [recipeVariationIngredientPatch.newFoodServingId],
      references: [foodServing.id],
    }),
  }),
);

export const recipeVariationStepPatch = pgTable(
  "recipe_variation_step_patch",
  {
    id: uuid("id").notNull(),
    recipeVariationId: uuid("recipe_variation_id").notNull(),
    recipeVariationVersion: integer("recipe_variation_version").notNull(),
    op: recipeVariationPatchOp("op").notNull(),
    baseSortIndex: text("base_sort_index"),
    newSortIndex: text("new_sort_index"),
    data: jsonb("data").$type<RecipeVariationStepPatchData>(),
  },
  (t) => [
    primaryKey({ name: "recipe_variation_step_patch_pk", columns: [t.id] }),
    unique("recipe_variation_step_patch_base_unique").on(
      t.recipeVariationId,
      t.recipeVariationVersion,
      t.baseSortIndex,
    ),
    unique("recipe_variation_step_patch_new_unique").on(
      t.recipeVariationId,
      t.recipeVariationVersion,
      t.newSortIndex,
    ),
    check(
      "recipe_variation_step_patch_base_required",
      sql`(
        (${t.op} = 'create' AND ${t.baseSortIndex} IS NULL)
        OR
        (${t.op} IN ('update', 'delete') AND ${t.baseSortIndex} IS NOT NULL)
      )`,
    ),
    check(
      "recipe_variation_step_patch_new_required",
      sql`(
        (${t.op} = 'create' AND ${t.newSortIndex} IS NOT NULL)
        OR
        (${t.op} = 'update')
        OR
        (${t.op} = 'delete' AND ${t.newSortIndex} IS NULL)
      )`,
    ),
    foreignKey({
      name: "recipe_variation_step_patch_variant_fk",
      columns: [t.recipeVariationId, t.recipeVariationVersion],
      foreignColumns: [recipeVariation.id, recipeVariation.version],
    }),
  ],
);

export const recipeVariationStepPatchRelations = relations(recipeVariationStepPatch, ({ one }) => ({
  variant: one(recipeVariation, {
    fields: [
      recipeVariationStepPatch.recipeVariationId,
      recipeVariationStepPatch.recipeVariationVersion,
    ],
    references: [recipeVariation.id, recipeVariation.version],
  }),
}));

export const recipeVariationNutrient = pgTable(
  "recipe_variation_nutrient",
  {
    recipeVariationId: uuid("recipe_variation_id").notNull(),
    recipeVariationVersion: integer("recipe_variation_version").notNull(),
    nutrientId: uuid("nutrient_id")
      .references(() => nutrient.id)
      .notNull(),
    amount: doublePrecision("amount").notNull(),
  },
  (t) => [
    primaryKey({
      name: "recipe_variation_nutrient_pk",
      columns: [t.recipeVariationId, t.recipeVariationVersion, t.nutrientId],
    }),
    foreignKey({
      name: "recipe_variation_nutrient_variant_fk",
      columns: [t.recipeVariationId, t.recipeVariationVersion],
      foreignColumns: [recipeVariation.id, recipeVariation.version],
    }),
  ],
);
